"use client";

import React, { ReactNode, useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { usePathname } from "next/navigation";

export interface Message {
  text: ReactNode;
  severity: "error" | "warning" | "info" | "success";
  routePath?: string;
}

interface AlertProps {
  message: Message | null;
  setMessage: (message: Message | null) => void;
  isMain?: boolean;
  differentPosition?: boolean;
}

const AlertComponent: React.FC<AlertProps> = ({
  message,
  setMessage,
  isMain = true,
  differentPosition = false,
}) => {
  const [visible, setVisible] = useState<boolean>(true);
  const pathname = usePathname();
  const hasMatchedRouteRef = useRef(false);

  useEffect(() => {
    if (message) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => setMessage(null), 300);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, setMessage]);

  useEffect(() => {
    if (!message?.routePath || !pathname) {
      hasMatchedRouteRef.current = false;
      return;
    }

    if (pathname === message.routePath) {
      hasMatchedRouteRef.current = true;
      return;
    }

    if (hasMatchedRouteRef.current) {
      setMessage(null);
      hasMatchedRouteRef.current = false;
    }
  }, [message?.routePath, pathname, setMessage]);

  const severityColors: Record<string, string> = {
    error: "red",
    warning: "#eab862",
    info: "blue",
    success: "green",
  };

  const color = message?.severity ? severityColors[message?.severity] : "";

  if (!message || (message.routePath && pathname && message.routePath !== pathname)) return null;
  return (
    <>
      <Box
        sx={{
          position: "fixed",
          right: isMain ? { xs: "6%", md: "2%" } : { xs: "4%", md: "4%" },
          maxWidth: "380px",
          top: differentPosition
            ? { xs: "1%", md: "7%" }
            : isMain
              ? { xs: "6%", md: "4%" }
              : { xs: "3%", md: "4%" },
          zIndex: 2000,
          ml: "15px",
        }}
      >
        <Alert
          id="alert"
          data-testid="alert"
          severity={message.severity}
          className={`transition-transform duration-300 ${visible ? "slide-in" : "slide-out"}`}
          sx={{
            ml: "auto",
            width: "fit-content",
            borderLeft: "5px solid",
            borderColor: color,
            bgcolor: "#fff",
            color,
            zIndex: 20,
            position: "relative",
          }}
          onClose={() => {
            setVisible(false);
            setTimeout(() => setMessage(null), 300);
          }}
        >
          {message.text}
        </Alert>
      </Box>
    </>
  );
};

export default AlertComponent;
